import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAuth } from "../context/AuthProvider";
import { NotificationService } from "../services/app/notification";
import { Progress } from "./ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from "./ui/sheet";

// Types
type Pergunta = {
  id: string;
  pergunta: string;
  tipo: "ESCALA" | "TEXTO";
};

// Schema de respostas simplificado para evitar erros
const RespostaSchema = z.object({
  perguntaId: z.string(),
  resposta: z.union([z.string(), z.number()]),
});

type Resposta = z.infer<typeof RespostaSchema>;

// Schema de formulário para validação
const formSchema = z.object({
  respostas: z.array(RespostaSchema),
  pesquisaId: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

export function PesquisaSatisfacao() {
  const notificationService = new NotificationService();
  const [etapaAtual, setEtapaAtual] = useState<number>(0);
  const [pesquisaAtual, setPesquisaAtual] = useState<number>(0);
  const [respostas, setRespostas] = useState<Resposta[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  const { handleSubmit, setValue, reset } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      respostas: [],
      pesquisaId: "",
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["pesquisa-satisfacao", user?.id],
    queryFn: async () => {
      const response = await notificationService.pesquisaSatisfacao();
      return response;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 20000, // Atualiza a cada 20 segundos
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (data && data?.length >= 1) {
      setIsOpen(true);
      // Configura o ID da pesquisa atual
      if (data[pesquisaAtual]?.id) {
        setValue("pesquisaId", data[pesquisaAtual].id);
      }
    }
  }, [data, pesquisaAtual, setValue]);

  // Atualiza as respostas no form sempre que elas mudarem
  useEffect(() => {
    setValue("respostas", respostas);
  }, [respostas, setValue]);

  // Função auxiliar para encontrar o tipo da pergunta
  const findPerguntaType = (perguntaId: string): "ESCALA" | "TEXTO" => {
    if (!data) return "TEXTO"; // Default seguro

    for (const pesquisa of data) {
      for (const perguntaItem of pesquisa.perguntas) {
        if (perguntaItem.pergunta.id === perguntaId) {
          return perguntaItem.pergunta.tipo;
        }
      }
    }

    return "TEXTO"; // Default seguro
  };

  // Formatar resposta conforme o tipo
  const formatarResposta = (
    resposta: string | number,
    tipo: "ESCALA" | "TEXTO"
  ): string | number => {
    if (tipo === "ESCALA") {
      // Se for espaço em branco ou vazio, retorna 0 como valor padrão
      if (typeof resposta === "string" && resposta.trim() === "") {
        return " ";
      }

      // Garantir que seja um número para ESCALA
      if (typeof resposta === "string") {
        const num = Number(resposta.trim());
        return isNaN(num) ? 0 : num; // Retorna 0 em vez de undefined
      }
      return resposta;
    } else {
      // Para tipo TEXTO, sempre retorna uma string
      if (resposta === "") {
        return " ";
      }
      return String(resposta);
    }
  };

  const handleResponder = (resposta: string | number, perguntaId: string) => {
    const novasRespostas = [...respostas];
    const index = novasRespostas.findIndex((r) => r.perguntaId === perguntaId);

    // Encontrar a pergunta para verificar seu tipo
    const perguntaType = findPerguntaType(perguntaId);

    // Formatar resposta conforme o tipo da pergunta
    const respostaFormatada = formatarResposta(resposta, perguntaType);

    if (index >= 0) {
      novasRespostas[index].resposta = respostaFormatada ?? " ";
    } else {
      novasRespostas.push({ perguntaId, resposta: respostaFormatada ?? " " });
    }

    setRespostas(novasRespostas);
  };

  const avancarEtapa = () => {
    const perguntas =
      data && data.length > 0 ? data[pesquisaAtual].perguntas : [];

    // validate if have respostas without answer, if it , add  " " to the resposta
    const respostasSemResposta = perguntas.filter(
      (pergunta) =>
        !respostas.some((r) => r.perguntaId === pergunta.pergunta.id)
    );
    if (respostasSemResposta.length > 0) {
      respostasSemResposta.forEach((pergunta) => {
        const tipo = findPerguntaType(pergunta.pergunta.id);
        const respostaFormatada = formatarResposta(" ", tipo);
        setRespostas((prev) => [
          ...prev,
          { perguntaId: pergunta.pergunta.id, resposta: respostaFormatada },
        ]);
      });
    }

    if (etapaAtual < perguntas.length) {
      setEtapaAtual(etapaAtual + 1);
    } else {
      // Submeter respostas e ir para a próxima pesquisa
      handleSubmit((formData) => {
        // Garante que temos o ID correto da pesquisa atual
        const dadosEnvio = {
          ...formData,
          pesquisaId: data?.[pesquisaAtual]?.id || "",
        };

        enviarRespostasMutation.mutate(dadosEnvio);
      })();
    }
  };

  const voltarEtapa = () => {
    if (etapaAtual > 0) {
      setEtapaAtual(etapaAtual - 1);
    }
  };

  const nextPesquisa = () => {
    if (data && pesquisaAtual < data.length - 1) {
      // Resetar formulário e respostas antes de mudar para próxima pesquisa
      reset({
        respostas: [],
        pesquisaId: data[pesquisaAtual + 1].id,
      });
      setRespostas([]);

      setPesquisaAtual(pesquisaAtual + 1);
      setEtapaAtual(0); // Volta para a etapa inicial da nova pesquisa
    }
  };

  // Mutation para enviar respostas
  const enviarRespostasMutation = useMutation({
    mutationFn: async (formData: FormValues) => {
      // Garantir que todas as respostas estejam no formato correto
      const respostasValidadas = formData.respostas.map((r) => {
        const tipo = findPerguntaType(r.perguntaId);
        const respostaFormatada = formatarResposta(r.resposta, tipo);
        return {
          perguntaId: r.perguntaId,
          resposta: respostaFormatada,
        };
      });

      const dadosFinais = {
        ...formData,
        respostas: respostasValidadas,
      };

      return await notificationService.enviarPesquisaSatisfacao(dadosFinais);
    },
    onSuccess: () => {
      if (data && pesquisaAtual < data.length - 1) {
        // Avança para a próxima pesquisa
        nextPesquisa();
      } else {
        // Finaliza todas as pesquisas e mostra tela de agradecimento
        setEtapaAtual(etapaAtual + 1);
      }
    },
  });

  // Mutation para recusar pesquisa
  const recusarPesquisaMutation = useMutation({
    mutationFn: async () => {
      return await notificationService.recusarPesquisaSatisfacao(
        data?.[pesquisaAtual].id || ""
      );
    },
    onSuccess: () => {
      setIsOpen(false);
      reset();
      setRespostas([]);
      setEtapaAtual(0);
      setPesquisaAtual(0);
    },
  });

  const renderEtapaInicial = () => {
    const pesquisa = data?.[pesquisaAtual];
    if (!pesquisa) return null;

    return (
      <div className="flex flex-col items-center p-6">
        <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-4">
          <span className="text-blue-500 text-4xl">📋</span>
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
          Pesquisa de Satisfação
        </h2>
        <p className="text-sm text-center text-gray-500 mb-6">
          Sua opinião é importante para nós
        </p>

        <div className="bg-blue-50 p-4 rounded-xl mb-6 w-full">
          <p className="text-base text-gray-700 leading-6">
            Pet: {pesquisa.agendamento_consulta.pet.nome} (
            {pesquisa.agendamento_consulta.pet.especie === "CACHORRO"
              ? "Cão"
              : "Gato"}
            )
          </p>
        </div>

        {data && data.length > 1 && (
          <p className="text-sm text-center text-gray-500 mb-6">
            Pesquisa {pesquisaAtual + 1} de {data.length}
          </p>
        )}

        <div className="flex gap-4 w-full">
          <Button
            variant="outline"
            className="flex-1 h-16"
            onClick={() => recusarPesquisaMutation.mutate()}
            disabled={recusarPesquisaMutation.isPending}
          >
            {recusarPesquisaMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Agora não"
            )}
          </Button>

          <Button
            className="flex-1 h-16"
            onClick={() => setEtapaAtual(1)}
          >
            Participar
          </Button>
        </div>
      </div>
    );
  };

  const renderEscala = (pergunta: Pergunta) => {
    const valores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const perguntaId = pergunta.id;
    const respostaAtual = respostas.find(
      (r) => r.perguntaId === perguntaId
    )?.resposta;

    // Função para obter cor baseada no valor
    const getColor = (valor: number) => {
      if (valor <= 3) return "bg-red-500";
      if (valor <= 6) return "bg-yellow-500";
      return "bg-green-500";
    };

    return (
      <div className="flex flex-col items-center p-6">
        <h3 className="text-lg font-semibold mb-2 text-center text-gray-800">
          {pergunta.pergunta}
        </h3>

        <p className="text-center text-xs text-gray-500 mb-8">
          Selecione um valor de 0 a 10
        </p>

        <div className="grid grid-cols-6 gap-2 mb-8">
          {valores.map((valor) => (
            <Button
              key={valor}
              variant={respostaAtual === valor ? "default" : "outline"}
              className={`h-16 w-16 rounded-full p-0 ${
                respostaAtual === valor ? getColor(valor) : ""
              }`}
              onClick={() => handleResponder(valor, perguntaId)}
            >
              <span className="text-lg font-bold">{valor}</span>
            </Button>
          ))}
        </div>

        <div className="flex gap-4 w-full">
          <Button
            variant="outline"
            className="flex-1 h-16"
            onClick={voltarEtapa}
          >
            Voltar
          </Button>

          <Button
            className="flex-1 h-16"
            onClick={avancarEtapa}
            disabled={respostaAtual === undefined}
          >
            Avançar
          </Button>
        </div>
      </div>
    );
  };

  const renderTexto = (pergunta: Pergunta) => {
    const perguntaId = pergunta.id;
    const respostaIndex = respostas.findIndex(
      (r) => r.perguntaId === perguntaId
    );
    const respostaAtual =
      respostaIndex >= 0 ? (respostas[respostaIndex].resposta as string) : " ";
    const perguntas =
      data && data.length > 0 ? data[pesquisaAtual].perguntas : [];

    return (
      <div className="flex flex-col items-center p-6">
        <h3 className="text-lg font-semibold mb-2 text-center text-gray-800">
          {pergunta.pergunta}
        </h3>

        <p className="text-center text-xs text-gray-500 mb-6">
          Sua opinião é muito importante para nós
        </p>

        <textarea
          className="w-full h-24 p-4 border border-gray-300 rounded-xl mb-8 resize-none"
          onChange={(e) => handleResponder(e.target.value || " ", perguntaId)}
          value={respostaAtual === " " ? "" : respostaAtual}
          placeholder="Digite sua resposta aqui..."
        />

        <div className="flex gap-4 w-full">
          <Button
            variant="outline"
            className="flex-1 h-16"
            onClick={voltarEtapa}
          >
            Voltar
          </Button>

          <Button className="flex-1 h-16" onClick={avancarEtapa}>
            {etapaAtual === perguntas.length ? "Finalizar" : "Avançar"}
          </Button>
        </div>
      </div>
    );
  };

  const renderAgradecimento = () => {
    const maisUmaPesquisa = data && pesquisaAtual < data.length - 1;

    return (
      <div className="flex flex-col items-center justify-center p-6">
        <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <Check className="text-green-500 text-5xl" />
        </div>

        <h2 className="text-2xl font-bold text-center mb-2 text-gray-800">
          Muito Obrigado!
        </h2>

        <p className="text-base text-center mb-10 text-gray-600 px-6 leading-6">
          Sua opinião é muito importante para melhorarmos continuamente nosso
          atendimento.
        </p>

        <div className="flex flex-col gap-4 w-full">
          {maisUmaPesquisa && (
            <Button
              className="w-full h-16"
              onClick={nextPesquisa}
            >
              Próxima Pesquisa
            </Button>
          )}

          <Button
            variant={maisUmaPesquisa ? "outline" : "default"}
            className="w-full h-16"
            onClick={() => {
              setIsOpen(false);
              // Resetar o estado ao fechar
              setTimeout(() => {
                reset();
                setRespostas([]);
                setEtapaAtual(0);
                setPesquisaAtual(0);
              }, 300);
            }}
          >
            {maisUmaPesquisa ? "Responder Depois" : "Fechar"}
          </Button>
        </div>
      </div>
    );
  };

  const renderCarregando = () => (
    <div className="flex flex-col items-center justify-center p-6">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      <p className="mt-4 text-gray-600">Carregando pesquisa...</p>
    </div>
  );

  useEffect(() => {
    if (!data || data.length === 0) {
      setIsOpen(false);
      reset();
      setRespostas([]);
      setEtapaAtual(0);
      setPesquisaAtual(0);
    }
  }, [data, reset]);

  const renderConteudo = () => {
    if (isLoading) {
      return renderCarregando();
    }

    if (!data || data.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-gray-700">
            Nenhuma pesquisa disponível no momento.
          </p>
        </div>
      );
    }

    if (etapaAtual === 0) {
      return renderEtapaInicial();
    }

    const perguntas = data[pesquisaAtual].perguntas;

    if (etapaAtual > perguntas.length) {
      return renderAgradecimento();
    }

    const perguntaItem = perguntas[etapaAtual - 1];
    if (!perguntaItem) return null;

    const perguntaAtual = perguntaItem.pergunta;

    if (perguntaAtual.tipo === "ESCALA") {
      return renderEscala(perguntaAtual);
    } else if (perguntaAtual.tipo === "TEXTO") {
      return renderTexto(perguntaAtual);
    }

    return null;
  };

  return (
    <Sheet open={isOpen} onOpenChange={() => {}} modal={true}>
      <SheetContent className="w-full sm:max-w-2xl h-[80vh] p-6" side="bottom">
        <SheetHeader className="mb-4">
          {/* Contador de pesquisas */}
          {data && data.length > 1 && etapaAtual > 0 && (
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Pesquisa {pesquisaAtual + 1} de {data.length}
              </p>
            </div>
          )}

          {/* Barra de progresso - visível somente quando estiver em uma pergunta */}
          {etapaAtual > 0 &&
            data &&
            data.length > 0 &&
            etapaAtual <= data[pesquisaAtual].perguntas.length && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">
                    Pergunta {etapaAtual} de{" "}
                    {data[pesquisaAtual].perguntas.length}
                  </span>
                  <span className="text-xs text-gray-500">
                    {Math.round(
                      (etapaAtual / data[pesquisaAtual].perguntas.length) * 100
                    )}
                    %
                  </span>
                </div>
                <Progress
                  value={
                    (etapaAtual / data[pesquisaAtual].perguntas.length) * 100
                  }
                  className="h-2"
                />
              </div>
            )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {renderConteudo()}
        </div>

        {/* Mensagens de estado para mutations */}
        {(enviarRespostasMutation.isPending ||
          recusarPesquisaMutation.isPending) && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="bg-white p-4 rounded-xl shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
              <p className="mt-2 text-gray-700 text-center">Processando...</p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
} 